import { useState, useEffect } from "react";
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaPaperPlane, FaWhatsapp, FaInstagram, FaLinkedin } from "react-icons/fa";
import emailjs from '@emailjs/browser';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [activeField, setActiveField] = useState(null);

  // Initialize EmailJS and scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    emailjs.init("oG7EbqN1Po7kZ8kxe");
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: "", message: "" });

    const serviceID = "service_m4i6eqg";
    const templateID = "template_7li6c2p";

    const submissionDate = new Date().toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Dubai'
    });

    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      phone: formData.phone,
      message: formData.message,
      submission_date: submissionDate,
      to_name: "The Dubai Properties Team"
    };

    emailjs.send(serviceID, templateID, templateParams)
      .then((response) => {
        setStatus({
          type: "success",
          message: "Thank you! Your message has been sent successfully. We'll get back to you within 24 hours."
        });
        setFormData({ name: "", email: "", phone: "", message: "" });
        setLoading(false);
        setTimeout(() => setStatus({ type: "", message: "" }), 5000);
      })
      .catch((error) => {
        console.error('FAILED...', error);
        setStatus({
          type: "error",
          message: "Oops! Something went wrong. Please try again or call us directly."
        });
        setLoading(false);
      });
  };

  const contactInfo = [
    {
      icon: <FaPhone />,
      title: "Call Us",
      content: "+91 8793764853",
      link: "tel:+918793764853",
      action: "Call now"
    },
    {
      icon: <FaEnvelope />,
      title: "Email Us",
      content: "info@dubaipropertybazaar18.com",
      link: "mailto:info@dubaipropertybazaar18.com",
      action: "Send email"
    },
    {
      icon: <FaMapMarkerAlt />,
      title: "Visit Us",
      content: "Blackthorn Chambers Office Suite# 18, 18th Floor, Gold Tower, Wave One Tower, L-2A, Pocket G, Sector 18, Noida, Uttar Pradesh 201301",
      link: "https://goo.gl/maps/xyz", // Placeholder link
      action: "Get directions"
    },
    {
      icon: <FaClock />,
      title: "Business Hours",
      content: "Monday - Sunday: 24/7 Support",
      link: null,
      action: "Always open"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Hero Section */}
      <div className="relative bg-[#0a0a0a] pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Abstract Background Shapes */}
        <div className="absolute top-0 right-0 -translate-y-12 translate-x-12 w-96 h-96 bg-[#c8a45e]/10 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 translate-y-12 -translate-x-12 w-64 h-64 bg-[#c8a45e]/10 rounded-full blur-[80px] pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <span className="inline-block py-1 px-3 rounded-full bg-[#c8a45e]/10 text-[#c8a45e] text-xs font-bold uppercase tracking-widest mb-6 border border-[#c8a45e]/20">
            Contact Support
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-6 tracking-tight">
            Get in <span className="text-[#c8a45e]">Touch</span>
          </h1>
          <p className="max-w-2xl mx-auto text-lg text-gray-400 font-light leading-relaxed">
            We're here to help you find your dream property in Dubai.
            Reach out to our expert team for personalized assistance and exclusive property viewings.
          </p>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-20 pb-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

          {/* Contact Info Cards (Left) */}
          <div className="lg:col-span-4 space-y-4">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className="group bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl hover:border-[#c8a45e]/30 transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#c8a45e]/10 flex items-center justify-center text-[#c8a45e] text-xl shrink-0 group-hover:scale-110 transition-transform duration-300">
                    {info.icon}
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-gray-900 text-lg mb-1">{info.title}</h3>
                    <p className="text-sm text-gray-500 mb-3 leading-relaxed">{info.content}</p>
                    {info.link && (
                      <a
                        href={info.link}
                        className="inline-flex items-center text-xs font-bold text-[#c8a45e] uppercase tracking-wide hover:underline"
                        target={info.title === "Visit Us" ? "_blank" : undefined}
                        rel="noreferrer"
                      >
                        {info.action} →
                      </a>
                    )}
                    {!info.link && (
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">
                        {info.action}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Social Links */}
            <div className="bg-[#0a0a0a] rounded-xl p-8 text-center shadow-lg relative overflow-hidden group">
              <div className="absolute inset-0 bg-[#c8a45e]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <h3 className="text-white font-display font-bold text-xl mb-6 relative z-10">Connect With Us</h3>
              <div className="flex justify-center gap-4 relative z-10">
                <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-[#c8a45e] hover:text-white transition-all duration-300 hover:scale-110">
                  <FaWhatsapp size={20} />
                </a>
                <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-[#c8a45e] hover:text-white transition-all duration-300 hover:scale-110">
                  <FaInstagram size={20} />
                </a>
                <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-[#c8a45e] hover:text-white transition-all duration-300 hover:scale-110">
                  <FaLinkedin size={20} />
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form (Right) */}
          <div className="lg:col-span-8">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10 border border-gray-100 h-full relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#c8a45e] to-transparent"></div>

              <div className="mb-8">
                <h2 className="text-2xl font-display font-bold text-gray-900 mb-2">Send us a Message</h2>
                <p className="text-gray-500">Fill out the form below and we'll get back to you shortly.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-sm font-bold text-gray-700 uppercase tracking-wider">Full Name</label>
                    <div className={`relative transition-all duration-300 ${activeField === 'name' ? 'scale-[1.02]' : ''}`}>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        onFocus={() => setActiveField('name')}
                        onBlur={() => setActiveField(null)}
                        required
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-[#c8a45e] focus:ring-1 focus:ring-[#c8a45e] transition-all"
                        placeholder="John Doe"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-bold text-gray-700 uppercase tracking-wider">Email Address</label>
                    <div className={`relative transition-all duration-300 ${activeField === 'email' ? 'scale-[1.02]' : ''}`}>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        onFocus={() => setActiveField('email')}
                        onBlur={() => setActiveField(null)}
                        required
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-[#c8a45e] focus:ring-1 focus:ring-[#c8a45e] transition-all"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="phone" className="text-sm font-bold text-gray-700 uppercase tracking-wider">Phone Number</label>
                  <div className={`relative transition-all duration-300 ${activeField === 'phone' ? 'scale-[1.02]' : ''}`}>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onFocus={() => setActiveField('phone')}
                      onBlur={() => setActiveField(null)}
                      required
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-[#c8a45e] focus:ring-1 focus:ring-[#c8a45e] transition-all"
                      placeholder="+971 50 123 4567"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="message" className="text-sm font-bold text-gray-700 uppercase tracking-wider">Message</label>
                  <div className={`relative transition-all duration-300 ${activeField === 'message' ? 'scale-[1.02]' : ''}`}>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      onFocus={() => setActiveField('message')}
                      onBlur={() => setActiveField(null)}
                      required
                      rows="6"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-[#c8a45e] focus:ring-1 focus:ring-[#c8a45e] transition-all resize-none"
                      placeholder="Tell us about your property requirements..."
                    ></textarea>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#0a0a0a] text-white font-display font-bold uppercase tracking-widest py-4 rounded-xl hover:bg-[#c8a45e] hover:shadow-lg hover:-translate-y-1 active:translate-y-0 transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed group"
                >
                  {loading ? (
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      Send Message
                      <FaPaperPlane className="group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>

                {status.message && (
                  <div
                    className={`p-4 rounded-xl text-sm font-medium flex items-center gap-3 animate-fade-in ${status.type === "success"
                      ? "bg-green-50 text-green-700 border border-green-200"
                      : "bg-red-50 text-red-700 border border-red-200"
                      }`}
                  >
                    {status.type === "success" ? (
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                        <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                        <svg className="w-3.5 h-3.5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </div>
                    )}
                    {status.message}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Map Section */}
      <section className="relative w-full h-[500px] bg-gray-100">
        <div className="absolute inset-0 filter grayscale hover:grayscale-0 transition-all duration-700">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3501.9747474131813!2d77.21699631508076!3d28.63124198242824!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cfd371d97c0b1%3A0x8c8d0da7c72e7e0c!2sConnaught%20Place%2C%20New%20Delhi%2C%20Delhi!5e0!3m2!1sen!2sin!4v1642000000000!5m2!1sen!2sin"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            title="Office Location"
          ></iframe>
        </div>

        {/* Map Overlay Card */}
        <div className="absolute bottom-8 left-4 right-4 md:left-auto md:right-8 md:w-80 bg-white p-6 rounded-xl shadow-2xl border border-gray-100">
          <h4 className="font-display font-bold text-lg mb-2 flex items-center gap-2">
            <FaMapMarkerAlt className="text-[#c8a45e]" />
            Visit Our Office
          </h4>
          <p className="text-sm text-gray-500 leading-relaxed mb-4">
            Blackthorn Chambers Office Suite# 18, 18th Floor, Gold Tower, Wave One Tower, Noida
          </p>
          <a
            href="#"
            className="block w-full py-2 bg-gray-100 hover:bg-[#c8a45e] hover:text-white text-gray-700 text-center rounded-lg text-sm font-bold transition-colors"
          >
            Get Directions
          </a>
        </div>
      </section>
    </div>
  );
}
